import React, { useContext } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Remove global layout wrapper to allow full-screen landing and auth pages
import { AuthContext } from "./features/Auth/context/AuthContext";

import ALandingPage from "./features/Landing/pages/ALandingPage";
import RegisterPage from "./features/Auth/pages/RegisterPage";
import LoginPage from "./features/Auth/pages/LoginPage";
import ForgotPasswordPage from "./features/Auth/pages/ForgotPasswordPage";
import ResetPasswordPage from "./features/Auth/pages/ResetPasswordPage";
import EmailVerificationPage from "./features/Auth/pages/EmailVerificationPage";
import WelcomeScreen from "./features/Auth/pages/WelcomeScreen";
import VideoDemosPage from "./features/Landing/pages/VideoDemosPage";
import AcceptInvitationPage from "./features/Dashboard/pages/AcceptInvitationPage";

// Dashboard components for each user type:
import OrganizationDashboard from "./features/Dashboard/pages/OrganizationDashboard";
import FormanDashboard from "./features/Dashboard/pages/FormanDashboard";
import ProjectManagerDashboard from "./features/Dashboard/pages/ProjectManagerDashboard";

// Onboarding for organizations
import OnboardingFlow from "./features/Onboarding/pages/OnboardingFlow";
// Feature modules
import TimesheetsPage from "./features/TimeSheets/TimesheetsPage";
import BusinessCardsPage from "./features/BusinessCardFlow/BusinessCardsPage";
import EquipmentRequestPage from "./features/EquipmentRequest/EquipmentRequestPage";
import CooperDashboard from "./features/EquipmentRequest/CooperDashboard";
import BidsModule from "./features/Bids/BidsModule";
// Projects
// ProjectsPage is now nested under dashboard
// import ProjectsPage from "./features/Projects/pages/ProjectsPage";



// Create a React Query client for caching server state
const queryClient = new QueryClient();
function ConstructionApp() {
  const { auth } = useContext(AuthContext);
  const isLoggedIn = !!auth.token;

  return (
    <QueryClientProvider client={queryClient}>
      <>
        <ToastContainer position="top-right" />
        <Routes>
      {/* Landing page: full-screen */}
      <Route index element={<ALandingPage />} />
      {/* Feature pages under /myteconstruction */}
      <Route path="timesheets"        element={<TimesheetsPage />} />
      <Route path="business-cards"    element={<BusinessCardsPage />} />
      <Route path="equipment-request" element={<EquipmentRequestPage />} />
      <Route path="cooperdashboard"   element={<CooperDashboard />} />
      <Route path="bids/*"            element={<BidsModule />} />

      {/* Public / Auth routes: full-screen */}
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="forgot" element={<ForgotPasswordPage />} />
      <Route path="reset" element={<ResetPasswordPage />} />
      <Route path="verify" element={<EmailVerificationPage />} />
      <Route path="welcome" element={<WelcomeScreen />} />
      <Route path="video-demos" element={<VideoDemosPage />} />

      {/* Invitation acceptance stays public */}
      <Route path="invite/accept" element={<AcceptInvitationPage />} />

      {/* Onboarding flow: auth required */}
      <Route
        path="onboarding"
        element={
          !isLoggedIn ? (
            <Navigate to="/myteconstruction/login" />
          ) : auth.user_type === 'Organization' && !auth.hasCompletedOnboarding ? (
            <OnboardingFlow />
          ) : (
            <Navigate to="/myteconstruction/dashboard" />
          )
        }
      />

      {/* Protected dashboards */}
      <Route
        path="dashboard/*"
        element={
          !isLoggedIn ? (
            <Navigate to="/myteconstruction/login" />
          ) : (
            <OrganizationDashboard />
          )
        }
      />
      <Route
        path="foreman-dashboard"
        element={
          !isLoggedIn ? (
            <Navigate to="/myteconstruction/login" />
          ) : (
            <FormanDashboard />
          )
        }
      />
      <Route
        path="project-manager-dashboard"
        element={
          !isLoggedIn ? (
            <Navigate to="/myteconstruction/login" />
          ) : (
            <ProjectManagerDashboard />
          )
        }
      />

      {/* Catch-all redirects to landing */}
      <Route path="*" element={<Navigate to="." />} />
      </Routes>
    </>
    </QueryClientProvider>
  );
}

export default ConstructionApp;
